import { createPortal } from "react-dom";
import { IconX, IconPackage, IconArrowsLeftRight } from "@tabler/icons-react";
import { SubmitHandler, useForm } from "react-hook-form";
import TextareaField from "../../../components/common/TextareaField";
import toast from "react-hot-toast";
import { useOrderReverseMutation } from "../../../apis/orderApis";
import { useLanguage } from "../../../context/LanguageContext";

// Define the expected form data type
interface ReverseOrderFormData {
  awbNumber: string;
}

const SectionHeader = ({ icon: Icon, title }: { icon: any; title: string }) => (
  <div className="flex items-center gap-2 border-b border-[#FBD037]/30 pb-2 mb-3">
    <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
      <Icon size={16} className="text-[#212121]" stroke={1.5} />
    </div>
    <p className="text-[#212121]/80 uppercase text-[14px] font-bold tracking-wide">
      {title}
    </p>
  </div>
);

export default function ReverseOrderModal({
  onClose,
  fetchData,
  isSidebar = false,
}: {
  onClose: () => void;
  fetchData?: () => void;
  isSidebar?: boolean;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ReverseOrderFormData>({
    defaultValues: {
      awbNumber: "",
    },
  });

  const { mutate: reverseOrderMutate, isPending: isReverseOrderPending } =
    useOrderReverseMutation();
  const { t } = useLanguage();

  const onSubmit: SubmitHandler<ReverseOrderFormData> = async (data: any) => {
    const awbArray = data.awbNumber
      .split("\n")
      .map((v: any) => v.trim())
      .filter((v: any) => v.length > 0);

    reverseOrderMutate(
      { orderId: awbArray },
      {
        onSuccess: (data: any) => {
          onClose();
          toast.success("Reverse Created Successfully");
          fetchData?.();
        },
        onError: (error: any) => {
          console.log(error, "error");
          toast.error(`${error}`);
        },
      },
    );
  };

  const content = (
    <form className="p-4" onSubmit={handleSubmit(onSubmit)}>
      {/* Header */}
      {/* <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#FBD037]/20 flex items-center justify-center text-[#212121] shrink-0">
            <IconPackage size={24} stroke={1.5} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#212121]">Create Reverse Order</h2>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={isReverseOrderPending}
            className="bg-[#FBD037] hover:bg-yellow-400 text-[#000000] border border-[#FBD037] flex items-center rounded-lg py-2 px-5 text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isReverseOrderPending ? "Submitting..." : "Submit"}
          </button>

          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700"
            title="Close"
          >
            <IconX size={20} />
          </button>
        </div>
      </div> */}

      {/* Body */}
      <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
        <SectionHeader icon={IconArrowsLeftRight} title={t("Reverse Order")} />
        <div className="w-full space-y-4">
          <TextareaField
            name="awbNumber"
            rows={8}
            label={t("Enter AWB Numbers (one per line)")}
            placeholder={t("Enter AWB Number 1") + "\n" + t("Enter AWB Number 2") + "\n..."}
            register={register}
            validation={{
              required: "AWB number(s) are required.",
              pattern: {
                value: /^[a-zA-Z0-9\s]+$/,
                message:
                  "Only alphanumeric characters and new lines are allowed.",
              },
            }}
            error={errors.awbNumber}
          />
        </div>
        <div className="flex items-center justify-end gap-3 border-t pt-2">
          <button
            type="submit"
            disabled={isReverseOrderPending}
            className="bg-[#FBD037] hover:bg-yellow-400 text-[#000000] border border-[#FBD037] flex items-center rounded-lg py-2 px-5 text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isReverseOrderPending ? t("Submitting...") : t("Submit")}
          </button>
        </div>
      </div>
    </form>
  );

  if (isSidebar) {
    return content;
  }

  return createPortal(
    <div className="fixed inset-0 w-screen h-screen bg-black/60 z-50 flex items-center justify-center font-sans">
      <div className="rounded-2xl max-w-2xl overflow-hidden">{content}</div>
    </div>,
    document.body,
  );
}
